import { FC, useContext, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { generatePath, Link, useNavigate, useParams } from 'react-router'

import { Bar } from '@ant-design/plots'
import { Flex, Table, Typography } from 'antd'
import { useResponsive } from 'antd-style'
import { ColumnProps } from 'antd/es/table'

import { useQuery } from '@tanstack/react-query'

import { Country } from '../../components/icon/Country'
import { Context as ConfigContext } from '../../providers/Config'
import routes, { generateSlug } from '../../routes'
import { client } from '../../services/motogp'
import { RiderNumber } from '../riders/RiderNumber'

import { Context } from './Context'

const StandingsView: FC = () => {
  const { state } = useContext(Context)
  const { mobile } = useResponsive()
  const { theme } = useContext(ConfigContext)
  const params = useParams()
  const navigate = useNavigate()
  const { isLoading, data } = useQuery({
    queryKey: ['session', state.seasonId, state.categoryId],
    queryFn: () => client.standings(state.seasonId, state.categoryId),
    enabled: !!state.categoryId && !!state.seasonId,
  })

  useEffect(() => {
    if ((state.params.year !== params.year || state.params.category !== params.category) && state.categoryId) {
      navigate(`/standings/${state.params.year}/${state.params.category}`)
    }
  }, [state.params, state.categoryId, navigate, params])

  const bikes: Record<string, string> = (data ?? [])
    .map((it) => it.constructor)
    .reduce((acc, cur) => ({ ...acc, ...{ [cur?.id ?? '']: cur?.name } }), {})
  const teams: Record<string, string> = (data ?? [])
    .map((it) => it.team)
    .filter(Boolean)
    .reduce((acc, cur) => ({ ...acc, ...{ [cur?.name ?? '']: cur?.name } }), {})

  const columnsDesktop: ColumnProps<MotoGP.Standings>[] = [
    {
      key: 'results.table.position',
      title: <FormattedMessage id={'results.table.position'} />,
      dataIndex: ['position'],
      width: 40,
      align: 'center',
      fixed: 'left',
    },
    {
      key: 'results.table.rider',
      title: <FormattedMessage id={'results.table.rider'} />,
      filterSearch: true,
      filters: data?.map((it) => ({
        text: it.rider.full_name,
        value: it.rider.full_name,
      })),
      onFilter: (value, record) => record.rider?.full_name === value,
      dataIndex: ['rider'],
      render: (it: MotoGP.Result['rider']) => (
        <Flex gap={4} align={'center'}>
          <RiderNumber number={it.number} />
          <Link to={generatePath(routes.rider, { slug: generateSlug(it.full_name) })}>{it.full_name}</Link>
        </Flex>
      ),
      width: 250,
    },
    {
      key: 'rider.country',
      title: <FormattedMessage id={'rider.country'} />,
      dataIndex: ['rider', 'country'],
      align: 'center',
      width: 50,
      render: (it: MotoGP.Result['rider']['country']) => <Country country={it?.iso} />,
    },
    {
      key: 'results.table.team',
      title: <FormattedMessage id={'results.table.team'} />,
      dataIndex: ['team', 'name'],
      render: (it: MotoGP.Result['team']['name']) => <Typography.Text ellipsis>{it}</Typography.Text>,
      filters: Object.entries(teams).map(([value, text]) => ({
        text,
        value,
      })),
      onFilter: (value, record) => record.team?.name === value,
      filterSearch: true,
    },
    {
      key: 'results.table.constructor',
      title: <FormattedMessage id={'results.table.constructor'} />,
      dataIndex: ['constructor', 'name'],
      filters: Object.entries(bikes).map(([value, text]) => ({
        text,
        value,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.constructor?.id === value,
    },

    {
      key: 'points',
      title: <FormattedMessage id={'results.table.points'} />,
      dataIndex: ['points'],
      align: 'center',
      width: 100,
    },
  ]

  const columnsMobile: ColumnProps<MotoGP.Standings>[] = [
    {
      key: 'position',
      title: '#',
      dataIndex: ['position'],
      align: 'center',
      width: 50,
    },
    {
      key: 'results.table.rider',
      title: <FormattedMessage id={'results.table.rider'} />,
      dataIndex: ['rider'],
      render: (value: MotoGP.Standings['rider'], item) => (
        <Flex vertical gap={4} style={{ width: '100%' }}>
          <Flex align={'center'} gap={4}>
            <RiderNumber color={item.team?.color} number={value.number} />
            <Link to={generatePath(routes.rider, { slug: generateSlug(value.full_name) })}>{value.full_name}</Link>
          </Flex>
          <Typography.Text strong>{item?.team?.name}</Typography.Text>
          <Typography.Text>{item?.constructor?.name}</Typography.Text>
        </Flex>
      ),
    },
    {
      key: 'results.table.points',
      title: <FormattedMessage id={'results.table.points'} />,
      dataIndex: ['points'],
      align: 'center',
      width: 75,
    },
  ]

  if (!state.seasonId || !state.categoryId || !data) return null

  return (
    <Flex vertical gap={16}>
      {state.view === 'table' ? (
        <Table size={'small'}
          style={{ overflowX: 'scroll' }}
          tableLayout={mobile ? 'fixed' : 'auto'}
          rowKey={(it) => it.id}
          pagination={false}
          loading={isLoading}
          dataSource={data}
          columns={
            mobile
              ? columnsMobile
              : columnsDesktop.filter((it) =>
                  Object.entries(teams).length === 0 ? it.key !== 'results.table.team' : it,
                )
          }
        />
      ) : (
        <Bar
          loading={isLoading}
          data={data?.filter((it) => it.points > 0)}
          theme={theme}
          xField={(it: MotoGP.Standings) => it.rider.full_name}
          yField={'points'}
          colorField={(it: MotoGP.Standings) => it.rider.full_name}
          sort={{
            reverse: true,
            by: 'y',
          }}
        />
      )}
    </Flex>
  )
}

export { StandingsView }
